import { usePostHog } from 'posthog-js/react';
import { FC, useEffect, useRef, useState } from 'react';

import { ImagesApp } from '../../constants/images';
import { useBreadCrumbsContext } from '../../hooks/contexts/breadCrumb.context';
import { useCartContext } from '../../hooks/contexts/cart.context';
import { useEventContext } from '../../hooks/contexts/event.context';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import useGuardian from '../../hooks/useGuardian';
import { computeCartTotals } from '../../utils/cartTotals';
import formatNumericValue from '../../utils/formatNumericValue';
import shouldDisableButton from '../../utils/shouldDisableButton';
import { BigButton } from '../Buttons';
import FeesBreakdown from '../EventDetails/Summary/FeesBreakdown';
import SelectedTickets, { TicketCart } from '../EventDetails/Summary/SelectedTickets';
import * as Styles from './summary-mobile.styles';

const SummaryMobile: FC = () => {
  const posthog = usePostHog();
  const [openDetails, setOpenDetails] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  /*
   * The sheet floats over the page, which reserves its height as bottom padding. That height
   * now depends on the fees block (its toggle, the locale, the screen width), so it is measured
   * and published instead of being hard-coded in each page.
   */
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;

    const root = document.documentElement;
    const observer = new ResizeObserver(([entry]) => {
      const height = Math.ceil(entry.target.getBoundingClientRect().height);
      root.style.setProperty(Styles.SUMMARY_MOBILE_HEIGHT_VAR, `${height}px`);
    });
    // The container renders unconditionally, so the ref is attached by the time effects run.
    observer.observe(containerRef.current!);

    return () => {
      observer.disconnect();
      root.style.removeProperty(Styles.SUMMARY_MOBILE_HEIGHT_VAR);
    };
  }, []);

  const { translate } = useI18nContext();
  const { currencyFormat, loading, event } = useEventContext();
  const { summaryData, getTotalTicketsInCart, namingData, cart, combosInCart, cartLoading } = useCartContext();
  const { isNamingPage } = useBreadCrumbsContext();
  const { navigateToNextRoute } = useGuardian();

  const { subtotal, serviceFee, total } = computeCartTotals({ summaryData, combosInCart });

  const hasMinimumTickets = cart.map((item) =>
    item.type.map((ticket) => ticket.quantityInCart < ticket.restrictions.minimum)
  );
  const findMinimumTickets = hasMinimumTickets.findIndex((item, i) => item[i] === true);
  const transferIsRequired = event?.attributes.ticketTransferRequired.value;
  const hasMandatoryFaceRecognition = namingData.some((curData) => curData.transferMandatoryFaceRecognition);
  const hasOptionalFaceRecognitionAndNoTransferable = namingData.some((curData) => (
    curData.faceRecognition && !curData.isTransferableGuestType
  ));

  const isTheNamingPageAndIsOk = namingData.reduce((acc, item) => {
    return item.holder ? acc : acc + 1;
  }, 0) > 0 && isNamingPage;

  const verifyDisableBtnRule = (
    transferIsRequired ||
    hasMandatoryFaceRecognition ||
    hasOptionalFaceRecognitionAndNoTransferable
  );

  const disableBtnInNamingPage = verifyDisableBtnRule ? isTheNamingPageAndIsOk : false;

  const handleDetails = () => {
    setOpenDetails(!openDetails);
  };

  const handleNavigate = () => {
    posthog?.capture('purchase:ticket_selection_continue_click');
    navigateToNextRoute();
  };

  const isDisabled = shouldDisableButton({
    isNamingPage,
    disableBtnInNamingPage,
    findMinimumTickets,
    cart,
    combosInCart,
    loading,
    cartLoading
  });

  return (
    <Styles.Container id="summary-mb" ref={containerRef}>
      <Styles.RowFullWidth $isCopyright={false}>
        <Styles.Column>
          <Styles.TextInfo $weight={500} size={12}>
            {getTotalTicketsInCart()}
            {' '}
            {translate(['STEPS', 'TXT4'])}
          </Styles.TextInfo>
        </Styles.Column>
        <Styles.Column>
          <button onClick={handleDetails}>
            <img
              src={openDetails ? ImagesApp.svg.arrowDown : ImagesApp.svg.arrowUp}
              alt="seta pra cima"
            />
          </button>
        </Styles.Column>
      </Styles.RowFullWidth>
      {openDetails && (
        <Styles.Detailing $isVisible={openDetails} id="summary-detailing">
          <SelectedTickets
            tickets={summaryData as unknown as TicketCart[]}
            combosInCart={combosInCart}
          />
        </Styles.Detailing>
      )}
      <Styles.Breakdown id="summary-mb-breakdown">
        <Styles.RowFullWidth $isCopyright={false}>
          <Styles.TextInfo $weight={500} size={12}>{translate(['STEPS', 'TXT14'])}</Styles.TextInfo>
          <Styles.TextInfo $weight={700} size={12} id="summary-mb-subtotal">
            {formatNumericValue({ value : subtotal, discount : 0, currencyFormat })}
          </Styles.TextInfo>
        </Styles.RowFullWidth>
        {/* Only a cart that carries a service fee shows the block: with none (free order, or a producer
           absorbing it) a "Taxas R$ 0,00" line just alarms the buyer (product decision, MKT-1043). */}
        {serviceFee > 0 && <FeesBreakdown serviceFee={serviceFee} currencyFormat={currencyFormat} />}
        <Styles.RowFullWidth $isCopyright={false}>
          <Styles.TextInfo $weight={700} size={16}>{translate(['SUMMARY_FEES', 'TOTAL'])}</Styles.TextInfo>
          <Styles.TextInfo $weight={700} size={16} id="summary-mb-total">
            {formatNumericValue({ value : total, discount : 0, currencyFormat })}
          </Styles.TextInfo>
        </Styles.RowFullWidth>
      </Styles.Breakdown>
      <Styles.RowFullWidth $isCopyright={false}>
        <BigButton
          onClick={handleNavigate}
          disabled={isDisabled}
        >
          {translate(['STEPS', 'TXT15'])}
        </BigButton>
      </Styles.RowFullWidth>
    </Styles.Container>
  );
};

export default SummaryMobile;
